import {inject, Injectable} from '@angular/core';
import {AuthenticationService} from '@modules/authentication';
import {CommunicationCenterService} from '@modules/communication-center';
import {ActivitiesService} from '@modules/activities/core/activities.service';
import {UserDataEntity} from '@modules/authentication/core/models/user-data-entity.type';

import {AuthorizationService} from '@modules/authorization';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {of} from 'rxjs/internal/observable/of';
import {AsyncRules, SyncRules} from '@modules/activities/core/models/rules';
import {LessonsConfigurationService} from '@modules/activities/core/lessons/services/lessons-configuration.service';
import {LessonGranuleEntity} from '@modules/activities/core/models';
import {Roles} from 'shared/models/roles';
import {has} from 'lodash-es';
import {ActionForLesson, LessonStates} from "shared/models/granule";


const INSTITUTION_MANAGER_ROLE_ID = '5';
const INSTITUTION_EDUCATOR_ROLE_ID = '4';

@Injectable({
    providedIn: 'root'
})
export class LessonAuthorizationService {
    private communicationCenter = inject(CommunicationCenterService)
    private authorizationService = inject(AuthorizationService);
    private authService = inject(AuthenticationService);
    private lessonsConfig = inject(LessonsConfigurationService);

    private reverseRoleMapping: { [roleNumber: number]: string } = {};
    private atLeastManagerRoles = ['manager', 'administrator'];
    private atLeastDirectorRoles = ['director', ...this.atLeastManagerRoles];
    private atLeastTrainerRoles = ['trainer', ...this.atLeastDirectorRoles];
    private userInstitutions: { admins: { uid: string, roles: Roles[] }[] }[];

    constructor() {

        this.communicationCenter.getRoom('authentication')
            .getSubject('roles')
            .subscribe((roleMapping: { [roleIdentifier: string]: number }) => {
                this.reverseRoleMapping = Object.keys(roleMapping).reduce((obj, roleName) => {
                    const roleId = roleMapping[roleName];
                    obj[roleId] = roleName;
                    return obj;
                }, {});
            });

        this.communicationCenter
            .getRoom('groups-management')
            .getSubject<LessonAuthorizationService['userInstitutions']>('institutionList')
            .subscribe(institutionList => {
                this.userInstitutions  = institutionList || [];
            });
    }

    public activeRulesOnStartup(): void {
        // Le module lesson est appelé plusieurs fois et on ne peut pas tester la préexistence des rules. Donc dans le doute, on supprime chaque rules avant de l'ajouter.
        this.authorizationService.removeRule(AsyncRules.AccessLessonList);
        this.authorizationService.removeRule(AsyncRules.DuplicateLesson);
        this.authorizationService.removeRule(AsyncRules.EditLesson);
        this.authorizationService.removeRule(AsyncRules.PreviewLesson);
        this.authorizationService.removeRule(SyncRules.AccessFormsModels);
        this.authorizationService.removeRule(AsyncRules.ArchiveLesson);
        this.authorizationService.removeRule(AsyncRules.UnarchiveLesson);
        this.authorizationService.removeRule(AsyncRules.PublishLesson);
        this.authorizationService.removeRule(AsyncRules.DeleteLesson);

        this.authorizationService.addRule(AsyncRules.AccessLessonList, (user) => {
            if (this.lessonsConfig.isInstanceMustCheckLicense()) {
                return this.isUserCanAccessLessonList$(user);
            } else {
                const allowedRoles = this.lessonsConfig.settings.accessMatrix.lessonsListing.view;
                const userRoles = this.userRoleNames(user);
                return of(allowedRoles.length > 0 && allowedRoles.some(role => userRoles.includes(role)));
            }
        });
        this.authorizationService.addRule(SyncRules.AccessFormsModels, (_user) => this.lessonsConfig.settings.isAccessibleFormsModels);
        this.authorizationService.addRule(AsyncRules.DuplicateLesson, this.isUserCanDuplicate.bind(this));
        this.authorizationService.addRule(AsyncRules.EditLesson, this.isUserCanEditLesson.bind(this));
        this.authorizationService.addRule(AsyncRules.ArchiveLesson, this.isUserCanArchive.bind(this));
        this.authorizationService.addRule(AsyncRules.UnarchiveLesson, this.isUserCanUnarchive.bind(this));
        this.authorizationService.addRule(AsyncRules.PublishLesson, this.isUserCanPublish.bind(this));
        this.authorizationService.addRule(AsyncRules.DeleteLesson, this.isUserCanDeleteLesson.bind(this));
        this.authorizationService.addRule(AsyncRules.PreviewLesson, this.isUserCanPreviewLesson.bind(this));
    }

    public isUserHasEducatorRightsInHisInstitutions(user: UserDataEntity): boolean {
        return this.userInstitutions.length > 0 && this.userInstitutions.every(institution =>
            institution.admins.some(admin =>
                admin.uid.toString() === user.id.toString()
                && has(admin.roles, INSTITUTION_EDUCATOR_ROLE_ID)
            )
        );
    }

    public isUserHasManagerRightsInHisInstitutions(user: UserDataEntity): boolean {
        return this.userInstitutions.length > 0 && this.userInstitutions.every(institution =>
            institution.admins.some(admin =>
                admin.uid.toString() === user.id.toString()
                && has(admin.roles, INSTITUTION_MANAGER_ROLE_ID)
            )
        );
    }

    private isUserCanAccessLessonList$(user): Observable<boolean> {
        return this.communicationCenter
            .getRoom('groups-management')
            .getSubject('institutionList').pipe(
                map(() => {
                    if (this.userIsAtLeastDirector(user)) {
                        return true;
                    } else if (this.userIsAtLeastTrainer(user)) {
                        // user can have Free license (no institution) or educator right
                        return !this.isUserHasManagerRightsInHisInstitutions(user) || this.isUserHasEducatorRightsInHisInstitutions(user);
                    }
                })
            );
    }

    private userIsAtLeastDirector(user: UserDataEntity): boolean {
        return this.atLeastDirectorRoles.some((role) => this.userRoleNames(user).includes(role));
    }

    private userIsAtLeastTrainer(user: UserDataEntity): boolean {
        return this.atLeastTrainerRoles.some((role) => this.userRoleNames(user).includes(role));
    }

    private userRoleNames(user: UserDataEntity): string[] {
        return user?.get('role')
            .map(id => this.reverseRoleMapping[id])
            .filter(str => !!str).map(str => str.toLowerCase()) || [];
    }

    private isUserCanEditLesson(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonEditionEnabled();
        const isAuthorManager = this.authService.isManager();
        const isLessonModel = _lesson?.get('model') === true;
        const isMyLesson = _lesson?.get('metadatas')?.author === _user.id;

        // Si t'as pas de _lesson, c'est que l'on test le droit en général
        if(!_lesson) {
            return of(isConfigOk === true);
        }
        // si auteur est Manager, verifier si la _lesson est un model et si archivé ou un brouillon
        const isLessonCanBeEditedAndAuthorManager = isAuthorManager && isLessonModel && _lesson.get('parcoursState') === LessonStates.draft;
        // Est-ce que je suis auteur de la _lesson ou (auteur manager et condition isLessonCanBeEditedAndAuthorManager)
        return of(isConfigOk && ((!isLessonModel && isMyLesson) || isLessonCanBeEditedAndAuthorManager));
    }
    private isUserCanDeleteLesson(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonDeleteEnabled();
        const isAuthorManager = this.authService.isManager();
        const isLessonModel = _lesson?.get('model') === true;
        const isMyLesson = _lesson?.get('metadatas')?.author === _user.id;

        // Si t'as pas de lesson, c'est que l'on test le droit en général
        if(!_lesson) {
            return of(isConfigOk === true);
        }
        // si auteur est Manager, verifier si la lesson est un model et si archivé ou un brouillon
        const isLessonCanBeDeletedAndAuthorManager = isAuthorManager && isLessonModel && (_lesson.get('parcoursState') === LessonStates.archived || _lesson.get('parcoursState') === LessonStates.draft);
        // Est-ce que je suis auteur de la lesson ou (auteur manager et condition isLessonCanBeDeletedAndAuthorManager)
        return of(isConfigOk && ((!isLessonModel && isMyLesson) || isLessonCanBeDeletedAndAuthorManager));
    }


    private isUserCanDuplicate(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonDuplicationEnabled();
        const lessonStateOnlyPublished = _lesson.get('parcoursState') === LessonStates.published;

        // il n'y a pas de raison qu'une lesson soit explicitement pas duplicable donc on ne la teste pas
        return of(isConfigOk && lessonStateOnlyPublished);
    }

    private isUserCanArchive(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonArchiveEnabled();
        const lessonState = _lesson.get('parcoursState') === LessonStates.published || _lesson.get('parcoursState') === LessonStates.draft;

        return of(isConfigOk && lessonState);
    }

    private isUserCanUnarchive(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonUnarchiveEnabled();
        const lessonState = _lesson.get('parcoursState') === LessonStates.archived

        return of(isConfigOk && lessonState);
    }

    private isUserCanPublish(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonPublishEnabled();
        const lessonState =  _lesson.get('parcoursState') === LessonStates.archived || _lesson.get('parcoursState') === LessonStates.draft;

        return of(isConfigOk && lessonState);
    }

    private isUserCanPreviewLesson(_user: UserDataEntity, _lesson: LessonGranuleEntity): Observable<boolean> {
        // Est-ce que c'est autorisé par les settings
        const isConfigOk = this.lessonsConfig.isLessonPreviewAccessible(this.authService.accessLevel as Roles);

        return of(isConfigOk);
    }
}
